import { Chip, Disclosure, type Column } from "@legal-500/legal500-design-system";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { companies, type Company } from "@/api/addressBook.service";
import {
  marketingFirmDiagnostics,
  type DiagnosticJurisdiction,
  type DiagnosticMember,
  type DiagnosticRow,
  type DiagnosticStep,
  type DiagnosticSubscription,
  type ExcludedRow,
  type FirmDiagnosticsLookup,
  type FirmDiagnosticsResponse,
  type NamedJurisdiction,
  type RegionMappingEntry,
} from "@/api/marketingFirmDiagnostics.service";
import { CompanyPicker } from "@/components/CompanyPicker/CompanyPicker";
import { DataTable } from "@/components/DataTable/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { PaginationControls } from "@/components/PaginationControls";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { useQuery } from "@/hooks/useApi";
import { usePermissions } from "@/hooks/usePermissions";
import { ROUTES } from "@/routes";
import {
  exclusionReasonLabel,
  JURISDICTION_STATUS,
  STEP_STATUS,
  subscriptionReasonLabel,
  subscriptionWarningLabel,
  VERDICT_TITLES,
} from "./labels";
import styles from "./FirmDiagnostics.module.css";

const FIRM_REF_PATTERN = /^[1-9]\d*$/;
const ROWS_PER_PAGE = 20;

/** The portal finds a firm by its firm ref, so say up front which companies have none. */
function firmRefAnnotation(company: Company): string {
  return company.firmRef ? `firm ref ${company.firmRef}` : "no firm ref";
}

function dash(value: string | number | null): string {
  return value === null || value === "" ? "—" : String(value);
}

function jurisdictionName(jurisdiction: NamedJurisdiction): string {
  return jurisdiction.name ?? `Unknown (${jurisdiction.countryId})`;
}

function jurisdictionList(jurisdictions: NamedJurisdiction[]): string {
  return jurisdictions.map(jurisdictionName).join(", ");
}

type Keyed<T> = T & { rowId: string };

/** A ranking and an accolade can share an id, so the table key carries the kind as well. */
function withRowId<T extends DiagnosticRow>(rows: T[]): Keyed<T>[] {
  return rows.map(row => ({ ...row, rowId: `${row.kind}:${row.id}` }));
}

/** A rows table paged on the client; the report already holds every row. */
function PagedRowsTable<T extends DiagnosticRow>({
  rows,
  columns,
  emptyMessage,
}: {
  rows: Keyed<T>[];
  columns: Column<Keyed<T>>[];
  emptyMessage: string;
}) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
  // A new report can have fewer rows; never leave the table on a page past the end.
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const endIndex = startIndex + ROWS_PER_PAGE;
  const pageRows = useMemo(() => rows.slice(startIndex, endIndex), [rows, startIndex, endIndex]);

  return (
    <>
      <DataTable data={pageRows} columns={columns} rowKey="rowId" emptyMessage={emptyMessage} />
      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        startIndex={startIndex}
        endIndex={endIndex}
        totalCount={rows.length}
        itemName="rows"
        onPageChange={setPage}
      />
    </>
  );
}

const ROW_KIND_LABELS: Record<DiagnosticRow["kind"], string> = {
  ranking: "Ranking",
  accolade: "Accolade",
  recommendation: "Recommended Lawyer",
};

function rowColumns<T extends DiagnosticRow>(): Column<Keyed<T>>[] {
  return [
    {
      header: "Kind",
      accessor: "kind",
      render: (_value, row) => ROW_KIND_LABELS[row.kind],
    },
    { header: "Name", accessor: "displayName" },
    {
      header: "Practice area",
      accessor: "practiceArea",
      render: (_value, row) => dash(row.practiceArea),
    },
    {
      header: "Country",
      accessor: "countryName",
      render: (_value, row) => dash(row.countryName),
    },
    {
      header: "Edition",
      accessor: "editionName",
      render: (_value, row) => dash(row.editionName),
    },
    {
      header: "Publication",
      accessor: "publicationName",
      render: (_value, row) => dash(row.publicationName),
    },
    {
      header: "Tier",
      accessor: "tier",
      render: (_value, row) => dash(row.tier),
    },
  ];
}

const visibleColumns: Column<Keyed<DiagnosticRow>>[] = [
  ...rowColumns<DiagnosticRow>(),
  {
    header: "Release",
    accessor: "earlyRelease",
    render: (_value, row) =>
      row.earlyRelease || row.preReleaseLaunchDate ? (
        <span className={styles.stack}>
          {row.earlyRelease && (
            <Chip size="small" color="warning">
              Early release
            </Chip>
          )}
          {row.preReleaseLaunchDate && (
            <span className={styles.muted}>
              Launches {new Date(row.preReleaseLaunchDate).toLocaleDateString()}
            </span>
          )}
        </span>
      ) : (
        <span className={styles.muted}>—</span>
      ),
  },
];

const excludedColumns: Column<Keyed<ExcludedRow>>[] = [
  ...rowColumns<ExcludedRow>(),
  {
    header: "Why it is hidden",
    accessor: "reasons",
    render: (_value, row) => (
      <ul className={styles.reasons}>
        {row.reasons.map(reason => (
          <li key={reason}>{exclusionReasonLabel(reason)}</li>
        ))}
      </ul>
    ),
  },
];

const memberColumns: Column<DiagnosticMember>[] = [
  { header: "Name", accessor: "name", render: (_value, row) => dash(row.name) },
  {
    header: "Email",
    accessor: "email",
    render: (_value, row) => dash(row.email),
  },
  {
    header: "Roles",
    accessor: "roles",
    render: (_value, row) => (row.roles.length > 0 ? row.roles.join(", ") : "None"),
  },
  {
    header: "Marketing role",
    accessor: "hasMarketingRole",
    render: (_value, row) =>
      row.hasMarketingRole ? (
        <Chip size="small" color="success">
          Yes
        </Chip>
      ) : (
        <Chip size="small" color="default" variant="outlined">
          No
        </Chip>
      ),
  },
];

/** Identical subscriptions share a groupKey; the table shows the first of each with a count. */
type SubscriptionGroup = DiagnosticSubscription & { count: number };

function groupSubscriptions(items: DiagnosticSubscription[]): SubscriptionGroup[] {
  const groups = new Map<string, SubscriptionGroup>();
  for (const item of items) {
    const group = groups.get(item.groupKey);
    if (group) group.count += 1;
    else groups.set(item.groupKey, { ...item, count: 1 });
  }
  return Array.from(groups.values());
}

function countryRegionLabel(subscription: DiagnosticSubscription): string {
  return subscription.siteStructureName ?? (subscription.siteStructureId || "Blank");
}

const subscriptionColumns: Column<SubscriptionGroup>[] = [
  {
    header: "Package",
    accessor: "packageName",
    render: (_value, row) => (
      <span className={styles.stack}>
        <span>
          {row.packageName}
          {row.count > 1 && <span className={styles.muted}> × {row.count}</span>}
        </span>
        <span className={styles.muted}>{row.productCode}</span>
      </span>
    ),
  },
  { header: "Status", accessor: "status" },
  {
    header: "Period",
    accessor: "startDate",
    render: (_value, row) => `${row.startDate} – ${row.endDate}`,
  },
  {
    header: "Country / Region",
    accessor: "siteStructureName",
    render: (_value, row) =>
      row.siteStructureId === null || row.siteStructureId === "" ? (
        <span className={styles.muted}>Blank</span>
      ) : (
        <span className={styles.stack}>
          {row.siteStructureName ?? "Unknown"}
          <span className={styles.muted}>{row.siteStructureId}</span>
        </span>
      ),
  },
  {
    header: "Marketing Toolkit feature",
    accessor: "carriesFeature",
    render: (_value, row) =>
      row.carriesFeature ? (
        <Chip size="small" color="success">
          Yes
        </Chip>
      ) : row.featureCodes.includes("marketing_tool_teaser") ? (
        <Chip size="small" color="warning">
          Teaser only
        </Chip>
      ) : (
        <Chip size="small" color="default" variant="outlined">
          No
        </Chip>
      ),
  },
  {
    header: "Jurisdictions",
    accessor: "countryIds",
    render: (_value, row) =>
      row.jurisdictions.length === 0 ? (
        <span className={styles.muted}>None</span>
      ) : (
        <Disclosure summary={String(row.jurisdictions.length)} className={styles.cellDisclosure}>
          <ul className={styles.plainList}>
            {row.jurisdictions.map(jurisdiction => (
              <li key={jurisdiction.countryId}>{jurisdictionName(jurisdiction)}</li>
            ))}
          </ul>
        </Disclosure>
      ),
  },
  { header: "Badges unlocked", accessor: "rowsUnlocked" },
  {
    header: "Only source for",
    accessor: "onlySourceRowCount",
    render: (_value, row) =>
      !row.contributes ? (
        <span className={styles.muted}>–</span>
      ) : row.onlySourceRowCount === 0 ? (
        <span className={`${styles.muted} ${styles.onlySourceNote}`}>
          0 (other subscriptions open the same jurisdictions)
        </span>
      ) : (
        row.onlySourceRowCount
      ),
  },
  {
    header: "Effect",
    accessor: "contributes",
    render: (_value, row) => (
      <span className={`${styles.stack} ${styles.effect}`}>
        {row.contributes ? (
          <Chip size="small" color="success">
            Counts
          </Chip>
        ) : (
          <Chip size="small" color="error" variant="outlined">
            Doesn't count
          </Chip>
        )}
        {row.reasons.length > 0 && (
          <ul className={styles.reasons}>
            {row.reasons.map(reason => (
              <li key={reason}>{subscriptionReasonLabel(reason)}</li>
            ))}
          </ul>
        )}
        {row.warnings.map(warning => (
          <span key={warning} className={styles.warning}>
            {subscriptionWarningLabel(warning)}
          </span>
        ))}
      </span>
    ),
  },
];

/** Every Country / Region setting that would open the jurisdiction; Global always does. */
function toOpenItText(row: DiagnosticJurisdiction): string {
  const { regions, ownCountry } = row.openedBy;
  if (regions.length === 0 && !ownCountry) return "Global only";
  const options = regions.map(region => `Region ${region.name}`);
  if (ownCountry) options.push(`Country ${row.countryName ?? `Unknown (${row.countryId})`}`);
  options.push("Global");
  return options.join(" or ");
}

/** The firm's subscriptions that open a jurisdiction, one entry per group of identical ones. */
function coveringGroups(
  coveredBy: string[],
  subscriptionsById: Map<string, DiagnosticSubscription>,
): SubscriptionGroup[] {
  return groupSubscriptions(
    coveredBy.flatMap(id => {
      const subscription = subscriptionsById.get(id);
      return subscription ? [subscription] : [];
    }),
  );
}

type KeyedJurisdiction = DiagnosticJurisdiction & { rowId: string };

function jurisdictionColumns(
  subscriptionsById: Map<string, DiagnosticSubscription>,
  subscriptionsFailed: boolean,
): Column<KeyedJurisdiction>[] {
  return [
    {
      header: "Jurisdiction",
      accessor: "countryName",
      render: (_value, row) =>
        row.countryId === null ? (
          <span className={styles.muted}>No jurisdiction</span>
        ) : (
          (row.countryName ?? String(row.countryId))
        ),
    },
    { header: "Rankings and accolades", accessor: "rowCount" },
    { header: "Shown", accessor: "shownCount" },
    {
      header: "Opened by the firm's subscriptions",
      accessor: "coveredBy",
      render: (_value, row) => {
        if (subscriptionsFailed) {
          return (
            <span className={`${styles.muted} ${styles.wrapCell}`}>
              Unknown, subscriptions couldn't be loaded
            </span>
          );
        }
        if (row.coveredBy.length === 0) return <span className={styles.muted}>None</span>;
        return (
          <ul className={`${styles.plainList} ${styles.wrapCell}`}>
            {coveringGroups(row.coveredBy, subscriptionsById).map(group => (
              <li key={group.groupKey}>
                {group.packageName} · {countryRegionLabel(group)} ·{" "}
                <span className={styles.nowrap}>
                  {group.startDate} – {group.endDate}
                </span>
                {group.count > 1 && <span className={styles.muted}> × {group.count}</span>}
              </li>
            ))}
          </ul>
        );
      },
    },
    {
      header: "To open it, set",
      accessor: "openedBy",
      render: (_value, row) =>
        row.coveredBy.length === 0 ? (
          <span className={styles.wrapCell}>{toOpenItText(row)}</span>
        ) : (
          <span className={styles.muted}>–</span>
        ),
    },
    {
      header: "Status",
      accessor: "status",
      render: (_value, row) => {
        const status = JURISDICTION_STATUS[row.status] ?? {
          label: row.status,
          color: "default" as const,
        };
        return (
          <Chip size="small" color={status.color} variant={status.variant}>
            {status.label}
          </Chip>
        );
      },
    },
  ];
}

const VERDICT_CLASSES: Record<FirmDiagnosticsResponse["verdict"]["outcome"], string> = {
  rows: styles.verdictRows,
  no_rows: styles.verdictNoRows,
  blocked: styles.verdictBlocked,
};

function StepItem({ step }: { step: DiagnosticStep }) {
  const status = STEP_STATUS[step.status] ?? {
    label: step.status,
    color: "default" as const,
  };
  const hasDetails = Object.keys(step.details ?? {}).length > 0;

  return (
    <li className={styles.step}>
      <div className={styles.stepHeader}>
        <Chip size="small" color={status.color} variant={status.variant}>
          {status.label}
        </Chip>
        <span className={styles.stepTitle}>{step.title}</span>
      </div>
      <p className={styles.stepSummary}>{step.summary}</p>
      {hasDetails && (
        <Disclosure summary="Details">
          <pre className={styles.json}>{JSON.stringify(step.details, null, 2)}</pre>
        </Disclosure>
      )}
    </li>
  );
}

function FirmSummary({ firm }: { firm: FirmDiagnosticsResponse["firm"] }) {
  // The organisation page needs the same grant the sidebar checks before listing it.
  const canOpenOrganisations = usePermissions().hasPermission("user.read_accounts");

  return (
    <section className={styles.card} aria-labelledby="firm-summary-title">
      <h2 id="firm-summary-title" className={styles.sectionTitle}>
        {firm.companyName ?? "Unknown firm"}
      </h2>
      <dl className={styles.infoGrid}>
        <div className={styles.infoItem}>
          <dt className={styles.infoLabel}>Company ID</dt>
          <dd className={styles.infoValue}>{dash(firm.companyId)}</dd>
        </div>
        <div className={styles.infoItem}>
          <dt className={styles.infoLabel}>Firm ref</dt>
          <dd className={styles.infoValue}>{dash(firm.firmRef)}</dd>
        </div>
        <div className={styles.infoItem}>
          <dt className={styles.infoLabel}>WordPress term ID</dt>
          <dd className={styles.infoValue}>{dash(firm.wordpressTermId)}</dd>
        </div>
        <div className={styles.infoItem}>
          <dt className={styles.infoLabel}>Business type</dt>
          <dd className={styles.infoValue}>{dash(firm.businessType)}</dd>
        </div>
        <div className={styles.infoItem}>
          <dt className={styles.infoLabel}>Organisations</dt>
          <dd className={styles.infoValue}>
            {firm.organisations.length === 0 ? (
              "None"
            ) : (
              <ul className={styles.plainList}>
                {firm.organisations.map(org => (
                  <li key={org.id}>
                    {canOpenOrganisations ? (
                      <Link to={ROUTES.users.organisationDetail(org.id)}>{org.name ?? org.id}</Link>
                    ) : (
                      (org.name ?? org.id)
                    )}
                  </li>
                ))}
              </ul>
            )}
          </dd>
        </div>
      </dl>
    </section>
  );
}

function SettingsUsed({ settings }: { settings: FirmDiagnosticsResponse["settings"] }) {
  return (
    <section className={styles.card} aria-labelledby="settings-title">
      <h2 id="settings-title" className={styles.sectionTitle}>
        Settings used
      </h2>
      <dl className={styles.infoGrid}>
        <div className={styles.infoItem}>
          <dt className={styles.infoLabel}>Early-release window</dt>
          <dd className={styles.infoValue}>
            {settings.earlyReleaseDays} days{" "}
            <code className={styles.code}>{settings.earlyReleaseDaysVariable}</code>
            <p className={styles.muted}>
              The value staff-bff reads. It must match the one account-bff reads.
            </p>
          </dd>
        </div>
        <div className={styles.infoItem}>
          <dt className={styles.infoLabel}>Flags</dt>
          <dd className={styles.infoValue}>
            {settings.flags.length === 0 ? (
              "None"
            ) : (
              <ul className={styles.plainList}>
                {settings.flags.map(flag => (
                  <li key={flag.key} className={styles.flag}>
                    <code className={styles.code}>{flag.key}</code>
                    {flag.enabled ? (
                      <Chip size="small" color="success">
                        Enabled
                      </Chip>
                    ) : (
                      <Chip size="small" color="default" variant="outlined">
                        Disabled
                      </Chip>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {settings.flagClientId && (
              <p className={styles.muted}>
                Flags evaluated for {settings.flagClientId}; per-user rollouts can differ.
              </p>
            )}
          </dd>
        </div>
      </dl>
    </section>
  );
}

function Subscriptions({
  subscriptions,
}: {
  subscriptions: FirmDiagnosticsResponse["subscriptions"];
}) {
  const groups = useMemo(() => groupSubscriptions(subscriptions.items), [subscriptions.items]);

  return (
    <section aria-labelledby="subscriptions-title">
      <h2 id="subscriptions-title" className={styles.sectionTitle}>
        Subscriptions
      </h2>
      <p className={styles.sectionDescription}>
        Every fulfilment subscription of the firm. A subscription counts when its status is
        complete, today falls between its start and end dates, its package includes the Marketing
        Toolkit and its Country / Region is recognised. Only subscriptions that count open
        jurisdictions. When several subscriptions count, the firm sees everything any of them opens.
        Only source for shows how many rows the firm would lose if that subscription ended.
      </p>
      {subscriptions.error !== null ? (
        <p role="alert" className={styles.errorBanner}>
          Could not read the subscriptions from fulfilment: {subscriptions.error}
        </p>
      ) : (
        <DataTable
          data={groups}
          columns={subscriptionColumns}
          className={styles.wideTable}
          rowKey="groupKey"
          emptyMessage="This firm has no subscriptions in fulfilment. The Marketing Toolkit shows nothing."
        />
      )}
    </section>
  );
}

function Jurisdictions({
  jurisdictions,
  subscriptions,
}: {
  jurisdictions: DiagnosticJurisdiction[];
  subscriptions: FirmDiagnosticsResponse["subscriptions"];
}) {
  const columns = useMemo(
    () =>
      jurisdictionColumns(
        new Map(subscriptions.items.map(sub => [sub.subscriptionId, sub])),
        subscriptions.error !== null,
      ),
    [subscriptions],
  );
  // A null country id is its own group, so the key needs a value for it.
  const data = useMemo(
    () => jurisdictions.map(row => ({ ...row, rowId: String(row.countryId ?? "none") })),
    [jurisdictions],
  );

  return (
    <section aria-labelledby="jurisdictions-title">
      <h2 id="jurisdictions-title" className={styles.sectionTitle}>
        Jurisdictions
      </h2>
      <p className={styles.sectionDescription}>
        Every jurisdiction where the firm holds rankings or accolades, and whether a subscription
        opens it.
      </p>
      <DataTable
        data={data}
        columns={columns}
        rowKey="rowId"
        emptyMessage="The firm holds no rankings or accolades the portal could show."
        className={styles.wideTable}
      />
      <p className={styles.muted}>
        Opened by the firm's subscriptions lists the subscriptions that give the firm this
        jurisdiction today. To open it, set lists the Country / Region values that would open a
        jurisdiction no subscription covers; any one of them is enough. Global removes the
        jurisdiction filter, so it opens every jurisdiction.
      </p>
    </section>
  );
}

function RegionMapping({ regions }: { regions: RegionMappingEntry[] }) {
  return (
    <section aria-label="Region mapping">
      <Disclosure summary="Region mapping: which jurisdictions each Country / Region opens">
        <p className={styles.sectionDescription}>
          A subscription set to a region opens that region's jurisdictions. A subscription set to a
          single country opens only that jurisdiction. Global opens everything. Worldwide is its own
          jurisdiction and currently opens nothing.
        </p>
        <div className={styles.regions}>
          {regions.map(region => (
            <div key={region.scopeId} className={styles.region}>
              <h3 className={styles.regionTitle}>
                {region.name} ({region.scopeId})
              </h3>
              <p className={styles.regionText}>
                Opens {region.jurisdictions.length} jurisdictions:{" "}
                <span className={styles.muted}>{jurisdictionList(region.jurisdictions)}</span>
              </p>
              {region.missingFromRegion.length > 0 && (
                <p className={styles.regionText}>
                  In the {region.guideName} but not opened by this region:{" "}
                  <span className={styles.muted}>{jurisdictionList(region.missingFromRegion)}</span>
                </p>
              )}
              {region.outsideGuide.length > 0 && (
                <p className={styles.regionText}>
                  Opened by this region but listed under another guide:{" "}
                  <span className={styles.muted}>{jurisdictionList(region.outsideGuide)}</span>
                </p>
              )}
            </div>
          ))}
        </div>
      </Disclosure>
    </section>
  );
}

function Report({ report }: { report: FirmDiagnosticsResponse }) {
  const rows = useMemo(() => withRowId(report.rows), [report.rows]);
  const excluded = useMemo(() => withRowId(report.excluded), [report.excluded]);

  return (
    <>
      <FirmSummary firm={report.firm} />
      <SettingsUsed settings={report.settings} />
      <Subscriptions subscriptions={report.subscriptions} />
      <Jurisdictions jurisdictions={report.jurisdictions} subscriptions={report.subscriptions} />

      <div
        className={`${styles.verdict} ${VERDICT_CLASSES[report.verdict.outcome]}`}
        data-outcome={report.verdict.outcome}
      >
        <h2 className={styles.verdictTitle}>{VERDICT_TITLES[report.verdict.outcome]}</h2>
        <p className={styles.verdictSummary}>{report.verdict.summary}</p>
        <p className={styles.muted}>Checked {new Date(report.generatedAt).toLocaleString()}</p>
      </div>

      <section aria-labelledby="steps-title">
        <h2 id="steps-title" className={styles.sectionTitle}>
          How the portal decided
        </h2>
        <ol className={styles.steps}>
          {report.steps.map(step => (
            <StepItem key={step.key} step={step} />
          ))}
        </ol>
      </section>

      <section aria-labelledby="rows-title">
        <h2 id="rows-title" className={styles.sectionTitle}>
          Rows the firm sees ({rows.length})
        </h2>
        <p className={styles.sectionDescription}>
          The rankings and accolades a user at this firm finds in the Marketing Toolkit, in the
          order the account portal lists them. Each row can be downloaded as a badge or as copy. An
          Early release marker or a launch date in the Release column means the row is visible only
          because the early-release window is open, before the edition launches.
        </p>
        <PagedRowsTable
          rows={rows}
          columns={visibleColumns}
          emptyMessage="The portal lists no rows for this firm."
        />
      </section>

      <section aria-labelledby="excluded-title">
        <h2 id="excluded-title" className={styles.sectionTitle}>
          Rows the firm does not see ({excluded.length})
        </h2>
        <p className={styles.sectionDescription}>
          Rankings and accolades recorded for this firm that the portal leaves out. The last column
          says why. A row can be hidden for more than one reason, and it stays hidden until every
          reason is gone.
        </p>
        <PagedRowsTable
          rows={excluded}
          columns={excludedColumns}
          emptyMessage="The portal hides none of this firm's rows."
        />
      </section>

      {report.members !== null && (
        <section aria-label={`Members (${report.members.length})`}>
          <Disclosure summary={`Members (${report.members.length})`}>
            <p className={styles.sectionDescription}>
              People in the firm's organisation. The toolkit checks access per person: the firm must
              hold the Marketing Toolkit, and the person needs a role that grants{" "}
              <code className={styles.code}>marketing.read</code> or{" "}
              <code className={styles.code}>marketing.write</code>. A member marked No does not see
              the toolkit, even when the firm has it. Only the first organisation is checked when a
              firm has several.
            </p>
            <DataTable
              data={report.members}
              columns={memberColumns}
              rowKey="userId"
              emptyMessage="The firm's organisations have no members."
            />
          </Disclosure>
        </section>
      )}

      <RegionMapping regions={report.regionMapping} />
    </>
  );
}

export function FirmDiagnosticsPage() {
  const { hasPermission } = usePermissions();
  const canRead = hasPermission("staff.marketing.diagnostics");
  const [searchParams, setSearchParams] = useSearchParams();

  const paramCompanyId = searchParams.get("companyId")?.trim() ?? "";
  const paramFirmRef = searchParams.get("firmRef")?.trim() ?? "";
  const paramOrganisationId = searchParams.get("organisationId")?.trim() ?? "";

  const firmRefInvalid =
    !paramCompanyId && paramFirmRef !== "" && !FIRM_REF_PATTERN.test(paramFirmRef);
  const lookup: FirmDiagnosticsLookup | null = paramCompanyId
    ? { companyId: paramCompanyId }
    : paramFirmRef
      ? firmRefInvalid
        ? null
        : { firmRef: paramFirmRef }
      : paramOrganisationId
        ? { organisationId: paramOrganisationId }
        : null;
  const lookupKey = lookup ? JSON.stringify(lookup) : "";

  const [company, setCompany] = useState<Company | null>(null);

  // A shared link carries only the company id: load the company so the picker shows its name.
  const { data: linkedCompany } = useQuery(
    () =>
      paramCompanyId && company?.id !== paramCompanyId
        ? companies.getById(paramCompanyId).then(response => response.data)
        : Promise.resolve(null),
    [paramCompanyId],
  );

  useEffect(() => {
    if (linkedCompany) setCompany(linkedCompany);
  }, [linkedCompany]);

  // A link to a firm ref or an organisation replaces any company picked before.
  useEffect(() => {
    if (!paramCompanyId) setCompany(null);
  }, [paramCompanyId]);

  const { data, loading, error } = useQuery(
    () => (lookup ? marketingFirmDiagnostics.get(lookup) : Promise.resolve(null)),
    [lookupKey],
  );

  function handleCompanyChange(next: Company | null) {
    setCompany(next);
    if (next) setSearchParams(new URLSearchParams({ companyId: next.id }));
  }

  if (!canRead) {
    return <UnauthorizedView />;
  }

  const lookupNote = paramCompanyId
    ? null
    : paramFirmRef
      ? `Showing firm ref ${paramFirmRef}.`
      : paramOrganisationId
        ? `Showing organisation ${paramOrganisationId}.`
        : null;

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Firm diagnostics"
        subtitle="Which rows the account portal lists for a firm, and why."
      />

      <div className={styles.lookup}>
        <div className={styles.picker}>
          <CompanyPicker
            value={company}
            onChange={handleCompanyChange}
            renderOptionAnnotation={firmRefAnnotation}
          />
        </div>
        {lookupNote && <p className={styles.muted}>{lookupNote}</p>}
      </div>

      <div className={styles.contentSection}>
        {firmRefInvalid ? (
          <p role="alert" className={styles.errorBanner}>
            The firm ref in the link must be a whole number greater than zero. Search for a company
            instead.
          </p>
        ) : !lookup ? (
          <p className={styles.emptyState}>
            Search for a company to see what the portal would list.
          </p>
        ) : loading ? (
          <p role="status" className={styles.muted}>
            Running the diagnostics…
          </p>
        ) : error ? (
          <p role="alert" className={styles.errorBanner}>
            {error.message}
          </p>
        ) : data ? (
          <Report report={data} />
        ) : null}
      </div>
    </div>
  );
}

export default FirmDiagnosticsPage;
